import { TimeUnits } from "./conversion-helpers";
import { nullCheckWrap } from "./function-helper";

export const convertToUtcDate = (date: Date): Date | null => {
    if(!date){
        return null;
    }
    const dateToConvert = new Date(date);

    const utcDate = new Date();
    utcDate.setUTCFullYear(dateToConvert.getFullYear());
    utcDate.setUTCMonth(dateToConvert.getMonth());
    utcDate.setUTCDate(dateToConvert.getDate());
    utcDate.setUTCHours(dateToConvert.getHours());
    utcDate.setUTCMinutes(dateToConvert.getMinutes());
    utcDate.setUTCSeconds(dateToConvert.getSeconds());
    utcDate.setUTCMilliseconds(dateToConvert.getMilliseconds());

    return utcDate;
}

type TrialWindow = {
    redactionTrialStartedAt?: Date | string | null,
    redactionTrialExpiresAt?: Date | string | null
};

/**
 * Normalises a redaction trial window to UTC in place, so a date read off the calendar as local midnight is
 * not posted as the previous evening while the clocks are forward. Only a value the user actually picked is
 * a Date; an untouched field is still the string the server sent, which is already UTC and must not be
 * shifted a second time.
 */
export const convertTrialWindowToUtc = (subscriptionDetails: TrialWindow): void => {
    if(!subscriptionDetails){
        return;
    }

    if(subscriptionDetails.redactionTrialStartedAt instanceof Date){
        subscriptionDetails.redactionTrialStartedAt = convertToUtcDate(subscriptionDetails.redactionTrialStartedAt);
    }

    if(subscriptionDetails.redactionTrialExpiresAt instanceof Date){
        subscriptionDetails.redactionTrialExpiresAt = convertToUtcDate(subscriptionDetails.redactionTrialExpiresAt);
    }
}

export const formatDate = (date: Date): string => {
    return Intl.DateTimeFormat('en-GB', {
        month: '2-digit',
        day: '2-digit',
        year: 'numeric'
    }).format(new Date(date));
}

export const getDaysInMonth = (year: number, month: number): number => {
    const daysInMonth = [
        31,
        (isLeapYear(year) ? 29 : 28), // February
        31,
        30,
        31,
        30,
        31,
        31,
        30,
        31,
        30,
        31
    ]

    return daysInMonth[month];
}

function isLeapYear(year) {
    return (year % 4 === 0 && year % 100 !== 0) || (year % 400 === 0);
}

export const getDateDiff = (start: Date, end: Date, unit: TimeUnits): number => {
    if(unit === 'months'){
        const startY = start.getFullYear();
        const endY = end.getFullYear();
        const startM = start.getMonth();
        const endM = end.getMonth();
 
        return (endM+12*endY)-(startM+12*startY);
    }
    
    if(unit === 'days'){
        const startTime = start.getTime();
        const endTime = end.getTime();
        const millisecondsPerDay = 1000 * 60 * 60 * 24;
        return Math.floor((endTime - startTime) / millisecondsPerDay);
    }
    
    if(unit === 'milliseconds'){
        const startTime = start.getTime();
        const endTime = end.getTime();
        return endTime - startTime;
    }
}

export const createDateObject = (date: Date): Date | null => {
    const initialiseDate = nullCheckWrap((d: Date) => new Date(d));
    return initialiseDate(date);
}